import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import Modal from 'buyside/components/presentation/Modal/modal';
import SavedSearchForm from 'buyside/components/presentation/SavedSearch/SavedSearchForm/savedSearchForm';

import {
  useCreateFundProfileSearchAlertMutation,
  useCreateIdeaSearchAlertMutation
} from 'interfaces/graphql-typings';

const SavedSearchButton: React.FC<{
  buttonSize?: BUTTON_SIZES;
  savedSearchNames?: string[];
  searchType: 'Idea' | 'FundProfile';
}> = ({ buttonSize, savedSearchNames, searchType }): React.ReactElement | null => {
  const [createIdeaSearchAlert] = useCreateIdeaSearchAlertMutation({
    update(cache, { data }) {
      const ideaSearchAlert = data?.createIdeaSearchAlert?.searchAlert;
      if (!ideaSearchAlert) return;

      cache.modify({
        fields: {
          ideasSavedSearchList: previousList => ({
            edges: [
              { node: ideaSearchAlert, __typename: 'IdeaSearchAlertEdge' },
              ...previousList.edges
            ]
          })
        }
      });
    }
  });

  const [createFundProfileSearchAlert] = useCreateFundProfileSearchAlertMutation({
    update(cache, { data }) {
      const fundProfileSearchAlert = data?.createEmployerProfileSearchAlert?.searchAlert;
      if (!fundProfileSearchAlert) return;

      cache.modify({
        fields: {
          fundProfilesSavedSearchList: previousList => ({
            edges: [
              { node: fundProfileSearchAlert, __typename: 'EmployerProfileSearchAlertEdge' },
              ...previousList.edges
            ]
          })
        }
      });
    }
  });

  const createSearchAlert =
    searchType === 'Idea' ? createIdeaSearchAlert : createFundProfileSearchAlert;

  const [modalOpen, setModalOpen] = useState(false);

  const { search } = useLocation();

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        onClick={() => setModalOpen(true)}
        size={buttonSize ? buttonSize : BUTTON_SIZES.SMALL}
        text="Save Search"
        type={BUTTON_TYPES.MAJOR}
      />
    ),
    [buttonSize]
  );

  if (!search) return null;

  const closeModal = (): void => setModalOpen(false);

  return (
    <Modal
      id="saved_search"
      isOpen={modalOpen}
      onClose={closeModal}
      renderOpenModalButton={renderOpenModalButton}
      title="Save This Search"
    >
      <SavedSearchForm
        closeModal={closeModal}
        handleCreateSearchAlert={createSearchAlert}
        savedSearchNames={savedSearchNames}
        search={search}
      />
    </Modal>
  );
};

export default SavedSearchButton;
